import React, { useState, useEffect, useRef } from "react";

export type MultiSelectOption = {
  display: string;
  data: string | number | boolean;
  id?: number;
};

type Props = {
  options: MultiSelectOption[];
  onOptionSelected: (
    selectedOption: MultiSelectOption,
    selectedIndex: number
  ) => void; // Callback prop
};

const Row: React.FC<Props> = ({ options, onOptionSelected }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [position, setPosition] = useState(0);
  const [width, setWidth] = useState(0); // State to store width
  const containerRef = useRef<HTMLDivElement>(null);
  let timer: ReturnType<typeof setTimeout> | null = null;

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const buttons = Array.from(container.querySelectorAll("button"));
      const activeButton =
        buttons[hoverIndex !== null ? hoverIndex : selectedIndex];
      if (activeButton) {
        const rect = activeButton.getBoundingClientRect();
        setPosition(rect.left - container.getBoundingClientRect().left);
        setWidth(rect.width); // Update width
      }
    }
  }, [selectedIndex, hoverIndex]);

  // Callback when an option is selected
  const handleOptionClick = (index: number) => {
    setSelectedIndex(index);
    onOptionSelected(options[index], index);
  };

  const handleMouseEnter = (index: number) => {
    if (timer) {
      clearTimeout(timer);
    }
    setHoverIndex(index);
  };

  // Add a delay on mouse leave
  const handleMouseLeave = () => {
    timer = setTimeout(() => {
      setHoverIndex(null);
    }, 200);  // 200ms delay
  };

  return (
    <div
      ref={containerRef}
      className="relative flex h-[40px] bg-gray-100 rounded-full overflow-hidden flex-shrink-0"
    >
      <div
        className={`absolute z-0 bg-sky-500 rounded-full transition-all duration-200 ease-in-out ${
          hoverIndex !== null ? "opacity-100" : "opacity-0"
        }`}
        style={{
          left: position,
          width: `${width}px`, 
          height: "100%",
        }}
      ></div>
      {options.map((option, index) => (
        <button
          key={index}
          className={`transition-all text-xs py-2 px-4 w-auto focus:outline-none z-10 text-center ${
            index === hoverIndex ? "text-white" : ""
          } ${index === selectedIndex ? "font-bold" : ""}`}
          onClick={() => handleOptionClick(index)}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        >
          {option.display}
        </button>
      ))}
    </div>
  );
};

export default Row;
